import { FC, useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import User from 'types/user';
import Utilities from 'utils/utilities';
import SelectOption from 'types/selectOption';
import roleList from 'types/roles';
import useAuth from 'hooks/useAuth';
import ConfirmatioDialog from 'ui-component/ConfirmationDialog';
import UserService from 'services/UserService';
import temperatureConfList from 'types/temperatureConf';

interface ChildComponentProps {
    selectedUser?: User;
    onSave(user: User): any;
    onDelete(user: User): any;
    onClose(): any;
    systemAdmin?: boolean;
}

const UserEdit: FC<ChildComponentProps> = (props) => {
    const { selectedUser, onSave, onDelete, onClose, systemAdmin } = props;
    const [open, setOpen] = useState(false);
    const [currentUser, setCurrentUser] = useState(selectedUser);
    const { user: loggedUser } = useAuth();
    const theme = useTheme();
    const { t } = useTranslation();
    const [dialogDelete, setDialogDelete] = useState(false);
    const roles: SelectOption[] = roleList(t);
    const mobileDevice = useMediaQuery(theme.breakpoints.down('md'));
    const utilities = new Utilities();
    const userService = new UserService();
    const temperatureConf: SelectOption[] = temperatureConfList();

    useEffect(() => {
        (async () => {
            if (selectedUser === undefined) setOpen(false);
            else {
                let defaultRole: string;
                if (selectedUser?.isSystemAdmin) {
                    defaultRole = 'System_Admin';
                } else {
                    defaultRole = selectedUser.role!;
                }
                const userCopy = {
                    ...selectedUser!,
                    role: defaultRole,
                    otpEnabled: await userService.isOtpEnabled(selectedUser.username!)
                };
                setCurrentUser(userCopy);
                setOpen(true);
            }
        })();
    }, [selectedUser]);

    const handleRole = (e: any) => {
        const { value } = utilities.getFormValue(e);
        let userCopy = null;

        if (value === 'System_Admin') {
            userCopy = {
                ...currentUser!,
                isSystemAdmin: true,
                role: ''
            };
        } else {
            userCopy = {
                ...currentUser!,
                role: value,
                isSystemAdmin: false
            };
        }
        setCurrentUser(userCopy);
    };

    return (
        <>
            <Dialog open={open} onClose={onClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                <DialogTitle id="alert-dialog-title">
                    {t('EDIT_USER')} {selectedUser?.username}
                </DialogTitle>
                <DialogContent sx={{ paddingTop: '6px!important', width: mobileDevice ? window.innerWidth - 65 : 500 }}>
                    <Grid container>
                        <Grid item xs={12} sx={{ mb: 2, mt: 1 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('NAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={selectedUser?.name}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            name: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    label={t('NAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel> {t('SURNAME')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={selectedUser?.surname}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            surname: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    label={t('SURNAME')}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ mb: 2 }}>
                            <FormControl fullWidth required>
                                <InputLabel> {t('Email')}</InputLabel>
                                <OutlinedInput
                                    defaultValue={selectedUser?.username}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            username: d.target.value
                                        };
                                        setCurrentUser(e);
                                    }}
                                    label={t('Email')}
                                />
                            </FormControl>
                        </Grid>
                        {!systemAdmin && (
                            <Grid item xs={12} sx={{ mb: 2 }}>
                                <FormControl fullWidth>
                                    <InputLabel> {t('COMPANY')}</InputLabel>
                                    <OutlinedInput
                                        defaultValue={selectedUser?.company}
                                        onChange={(d) => {
                                            const e = {
                                                ...currentUser!,
                                                company: d.target.value
                                            };
                                            setCurrentUser(e);
                                        }}
                                        type="text"
                                        label={t('COMPANY')}
                                    />
                                </FormControl>
                            </Grid>
                        )}
                        <Grid item xs={6} sx={{ mb: 2 }}>
                            <FormControl fullWidth>
                                <InputLabel>{t('ROLE')}</InputLabel>
                                <Select
                                    label={t('ROLE')}
                                    name="frequency"
                                    onChange={handleRole}
                                    sx={{ color: 'white' }}
                                    value={currentUser?.role}
                                    defaultValue={selectedUser?.role}
                                    disabled={loggedUser?.isSystemAdmin && selectedUser?.isSystemAdmin}
                                >
                                    {roles.map((role: any) => {
                                        if (role.value === 'System_Admin' && !currentUser?.isSystemAdmin) {
                                            return null;
                                        }
                                        return (
                                            <MenuItem value={role.value} key={role.value}>
                                                {t(role.label)}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item md={6} xs={6} sx={{ mb: 2, textAlign: 'center' }}>
                            <FormControl fullWidth required>
                                <>{t('ENABLE_OTP')}</>
                                <Checkbox
                                    disabled={currentUser?.role === 'System_Admin'}
                                    checked={currentUser?.otpEnabled || currentUser?.role === 'System_Admin'}
                                    onChange={(d) => {
                                        const e = {
                                            ...currentUser!,
                                            otpEnabled: d.target.checked
                                        };
                                        setCurrentUser(e);
                                    }}
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl fullWidth required>
                                <InputLabel> {t('TEMPERATURE_UNIT')}</InputLabel>
                                <Select
                                    label={t('TEMPERATURE_UNIT')}
                                    name="temperatureUnit"
                                    onChange={(e) => {
                                        const newValue = e.target.value;
                                        const updatedUser = {
                                            ...currentUser!,
                                            preferredTemperatureUnit: newValue
                                        };
                                        setCurrentUser(updatedUser);
                                    }}
                                    sx={{ color: 'white' }}
                                    value={currentUser?.preferredTemperatureUnit!}
                                >
                                    {temperatureConf.map((temperature) => (
                                        <MenuItem value={temperature.value} key={temperature.value}>
                                            {t(temperature.label)}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Typography marginTop="5px"> * {t('MANDATORY_FIELDS')}</Typography>
                </DialogContent>
                <DialogActions>
                    <Grid
                        container
                        sx={{ alignItems: 'center', justifyContent: 'space-between', paddingLeft: '15px', paddingRight: '15px' }}
                    >
                        <Grid item>
                            <Button
                                style={{
                                    backgroundColor: theme.palette.error.main,
                                    borderRadius: 7,
                                    border: '2px solid',
                                    borderColor: theme.palette.error.main
                                }}
                                onClick={() => setDialogDelete(true)}
                            >
                                <Typography sx={{ color: 'white' }}> {t('DELETE')}</Typography>
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                                onClick={onClose}
                                autoFocus
                                style={{
                                    border: '2px solid',
                                    borderColor: theme.palette.secondary.main,
                                    borderRadius: 7,
                                    marginRight: 10
                                }}
                            >
                                <Typography sx={{ color: theme.palette.primary.main }}> {t('CANCEL')}</Typography>
                            </Button>
                            <Button
                                onClick={() => onSave(currentUser!)}
                                style={{
                                    border: '2px solid',
                                    borderColor: theme.palette.primary.main,
                                    borderRadius: 7,
                                    background: theme.palette.primary.main
                                }}
                            >
                                <Typography sx={{ color: '#fff' }}> {t('SAVE')}</Typography>
                            </Button>
                        </Grid>
                    </Grid>
                </DialogActions>
            </Dialog>
            <ConfirmatioDialog
                isVisible={dialogDelete}
                onClose={() => {
                    setDialogDelete(false);
                }}
                onConfirm={() => {
                    setDialogDelete(false);
                    onDelete(selectedUser!);
                }}
            />
        </>
    );
};

export default UserEdit;
